// This is the single binding contract for template JSON and print payloads.
// Change sourcePath values here when a print endpoint returns a different shape.
export const bindingContracts = {
  candidates: {
    label: 'Candidates',
    recordIdentity: { bindingPath: 'candidate.pk_Candidate', sourcePath: 'pk_Candidate' },
    groups: [
      {
        label: 'Candidate',
        fields: [
          { bindingPath: 'candidate.FirstName', label: 'First Name', sourcePath: 'FirstName' },
          { bindingPath: 'candidate.LastName', label: 'Last Name', sourcePath: 'LastName' },
          { bindingPath: 'candidate.Email', label: 'Email', sourcePath: 'Email' },
          { bindingPath: 'candidate.CandidateSubHeading', label: 'Subheading', sourcePath: 'CandidateSubHeading' },
          { bindingPath: 'candidate.DateOfInterview', label: 'Interview Date', sourcePath: 'DateOfInterview' },
        ],
      },
    ],
  },
  evaluators: {
    label: 'Evaluators',
    recordIdentity: { bindingPath: 'evaluator.metaData.pk_User', sourcePath: 'metaData.pk_User' },
    groups: [
      {
        label: 'Evaluator',
        fields: [
          { bindingPath: 'evaluator.metaData.UserFirst', label: 'First Name', sourcePath: 'metaData.UserFirst' },
          { bindingPath: 'evaluator.metaData.UserLast', label: 'Last Name', sourcePath: 'metaData.UserLast' },
          { bindingPath: 'evaluator.metaData.pk_User', label: 'Evaluator ID', sourcePath: 'metaData.pk_User' },
        ],
      },
      {
        label: 'Interview',
        fields: [
          { bindingPath: 'interview.DateOfInterview', label: 'Interview Date', sourcePath: 'interview.DateOfInterview' },
          { bindingPath: 'interview.Label', label: 'Session Label', sourcePath: 'interview.Label' },
          { bindingPath: 'interview.StartTime', label: 'Session Start Time', sourcePath: 'interview.StartTime' },
        ],
      },
    ],
    tables: [
      {
        bindingPath: 'schedule.slots',
        label: 'Evaluator Schedule Slots',
        sourcePath: 'body',
        columns: [
          { bindingPath: 'StartTime', label: 'Slot Start Time', sourcePath: 'StartTime' },
          { bindingPath: 'Event', label: 'Slot Event', sourcePath: 'Event' },
        ],
      },
    ],
  },
  program: {
    groups: [
      {
        label: 'Program',
        fields: [
          { bindingPath: 'department.DepartmentName', label: 'Program Name', sourcePath: 'DepartmentName' },
          { bindingPath: 'department.OrganizationName', label: 'Organization Name', sourcePath: 'OrganizationName' },
          { bindingPath: 'department.TimeZone', label: 'Program Time Zone', sourcePath: 'TimeZone' },
          { bindingPath: 'season.SeasonName', label: 'Season Name', sourcePath: 'SeasonName' },
        ],
      },
    ],
  },
};

export const evaluatorSchedulePreviewRecords = [
  {
    body: [
      { Event: 'Interview: Avery Morgan', StartTime: '08:00 AM' },
      { Event: 'Interview: Jordan Lee', StartTime: '08:15 AM' },
      { Event: 'Break', StartTime: '08:30 AM' },
    ],
    interview: { DateOfInterview: 'Jun 29, 2026', Label: 'Morning Interviews', StartTime: '08:00 AM' },
    metaData: { pk_User: 10001, UserFirst: 'Rick', UserLast: 'Richtor' },
  },
  {
    body: [
      { Event: 'Interview: Sam Patel', StartTime: '08:00 AM' },
      { Event: 'Flex Event: Faculty Meeting', StartTime: '08:15 AM' },
      { Event: 'Interview: Taylor Chen', StartTime: '08:30 AM' },
    ],
    interview: { DateOfInterview: 'Jun 29, 2026', Label: 'Morning Interviews', StartTime: '08:00 AM' },
    metaData: { pk_User: 10002, UserFirst: 'Aaron', UserLast: 'Anderson' },
  },
];

export const resolveBindingValue = (record, path) => {
  if (!path) {
    return undefined;
  }

  return path.split('.').reduce((value, key) => {
    return value == null ? undefined : value[key];
  }, record);
};

const setBindingValue = (record, path, value) => {
  const pathKeys = path.split('.');
  const lastKey = pathKeys.pop();
  const target = pathKeys.reduce((currentValue, key) => {
    currentValue[key] = currentValue[key] || {};
    return currentValue[key];
  }, record);

  target[lastKey] = value;
};

const normalizeFields = (bindingData, fields, sourceRecord) => {
  fields.forEach((field) => {
    setBindingValue(bindingData, field.bindingPath, resolveBindingValue(sourceRecord, field.sourcePath));
  });
};

const getSourceGroups = (dataSource) => {
  return bindingContracts[dataSource]?.groups || [];
};

export const getBindingGroups = (dataSource, regionName) => {
  const programGroups = bindingContracts.program.groups;

  return regionName === 'dataContent' ? [...getSourceGroups(dataSource), ...programGroups] : programGroups;
};

export const getBindingLabel = (bindingPath) => {
  const groups = [...bindingContracts.program.groups, ...getSourceGroups('candidates'), ...getSourceGroups('evaluators')];
  const bindingGroup = groups.find((group) => {
    return group.fields.some((field) => {
      return field.bindingPath === bindingPath;
    });
  });
  const binding = bindingGroup?.fields.find((field) => {
    return field.bindingPath === bindingPath;
  });

  return binding ? `${bindingGroup.label}: ${binding.label}` : '';
};

export const getTableBindings = (dataSource) => {
  return bindingContracts[dataSource]?.tables || [];
};

export const getTableBindingFields = (dataSource, bindingPath) => {
  const tableBinding = getTableBindings(dataSource).find((table) => {
    return table.bindingPath === bindingPath;
  });

  return tableBinding?.columns || [];
};

export const getBindingRecordId = (dataSource, bindingData) => {
  return resolveBindingValue(bindingData, bindingContracts[dataSource]?.recordIdentity?.bindingPath);
};

export const createBindingData = ({ candidate, department, evaluator, season }) => {
  const bindingData = {};

  normalizeFields(bindingData, bindingContracts.program.groups[0].fields.slice(0, 3), department || {});
  normalizeFields(bindingData, bindingContracts.program.groups[0].fields.slice(3), season || {});
  normalizeFields(bindingData, getSourceGroups('candidates')[0].fields, candidate || {});
  setBindingValue(
    bindingData,
    bindingContracts.candidates.recordIdentity.bindingPath,
    resolveBindingValue(candidate, bindingContracts.candidates.recordIdentity.sourcePath),
  );

  getSourceGroups('evaluators').forEach((group) => {
    normalizeFields(bindingData, group.fields, evaluator || {});
  });
  setBindingValue(
    bindingData,
    bindingContracts.evaluators.recordIdentity.bindingPath,
    resolveBindingValue(evaluator, bindingContracts.evaluators.recordIdentity.sourcePath),
  );

  getTableBindings('evaluators').forEach((table) => {
    setBindingValue(bindingData, table.bindingPath, resolveBindingValue(evaluator, table.sourcePath) || []);
  });

  return bindingData;
};
